import React, { createContext, useContext, useState, useEffect } from 'react';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

interface CookieContextType {
  preferences: CookiePreferences;
  hasConsented: boolean;
  openCookiePreferences: () => void;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false
};

// Create context with default values
const CookieContext = createContext<CookieContextType>({
  preferences: defaultPreferences,
  hasConsented: false,
  openCookiePreferences: () => {},
});

export const useCookieConsent = () => useContext(CookieContext);

interface CookieProviderProps {
  children: React.ReactNode;
}

export const CookieProvider: React.FC<CookieProviderProps> = ({ children }) => {
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [hasConsented, setHasConsented] = useState<boolean>(false);
  const [showPreferences, setShowPreferences] = useState<boolean>(false);

  // Load saved preferences on mount
  useEffect(() => {
    const consentGiven = localStorage.getItem('cookieConsent');
    
    if (consentGiven === 'true') {
      setHasConsented(true);
      
      try {
        const savedPreferences = JSON.parse(localStorage.getItem('cookiePreferences') || '');
        if (savedPreferences) {
          setPreferences(savedPreferences);
        }
      } catch (e) {
        // If parsing fails, use defaults
      }
    }
  }, []);

  const openCookiePreferences = () => {
    // This function will be exposed through the context
    // The actual implementation to show the modal will be in the CookieConsent component
    setShowPreferences(true);
    
    // Dispatch a custom event that CookieConsent component will listen for
    const event = new CustomEvent('openCookiePreferences');
    window.dispatchEvent(event);
  };

  const value = {
    preferences,
    hasConsented,
    openCookiePreferences,
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};

export default CookieContext;